const COLOR_OVERRIDES_PARAM = 'layerColors';
const COLOR_OVERRIDE_PARAM = 'layerColor';
const PAIR_SEPARATOR = ',';
const VALUE_SEPARATOR = '|';
const HEX_COLOR_PATTERN = /^#?[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/;
const SLD_STYLE = {
    pointSize: 8,
    pointStrokeWidth: 1.5,
    pointFillOpacity: 0.9,
    lineWidth: 3,
    polygonStrokeWidth: 2,
    fillOpacity: 0.35,
    strokeOpacity: 1,
};
let cachedSearch = '';
let cachedOverrides = null;
const escapeXml = (value) => {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
};
const normalizeHexColor = (value) => {
    const trimmed = value.trim();
    if (!trimmed || !HEX_COLOR_PATTERN.test(trimmed)) {
        return null;
    }
    let normalized = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
    if (normalized.length === 4) {
        normalized = `#${normalized.slice(1).split('').map((char) => `${char}${char}`).join('')}`;
    }
    return normalized.toLowerCase();
};
const parseLayerColorPair = (pair) => {
    const trimmed = pair.trim();
    if (!trimmed)
        return null;
    const separatorIndex = trimmed.indexOf(VALUE_SEPARATOR);
    if (separatorIndex <= 0 || separatorIndex === trimmed.length - 1) {
        return null;
    }
    const layerId = trimmed.slice(0, separatorIndex).trim();
    const colorRaw = trimmed.slice(separatorIndex + 1).trim();
    if (!layerId || !colorRaw)
        return null;
    const normalizedColor = normalizeHexColor(colorRaw);
    if (!normalizedColor) {
        return null;
    }
    return { id: layerId, color: normalizedColor };
};
const collectOverrides = (params) => {
    const overrides = {};
    const combined = params.get(COLOR_OVERRIDES_PARAM);
    if (combined) {
        combined.split(PAIR_SEPARATOR).forEach((pair) => {
            const parsed = parseLayerColorPair(pair);
            if (parsed) {
                overrides[parsed.id] = parsed.color;
            }
        });
    }
    const singleEntries = params.getAll(COLOR_OVERRIDE_PARAM);
    singleEntries.forEach((entry) => {
        entry.split(PAIR_SEPARATOR).forEach((pair) => {
            const parsed = parseLayerColorPair(pair);
            if (parsed) {
                overrides[parsed.id] = parsed.color;
            }
        });
    });
    return overrides;
};
const getOverrides = () => {
    if (typeof window === 'undefined') {
        return {};
    }
    const search = window.location.search;
    if (cachedOverrides && cachedSearch === search) {
        return cachedOverrides;
    }
    cachedSearch = search;
    cachedOverrides = collectOverrides(new URLSearchParams(search));
    return cachedOverrides;
};
const getOverrideColor = (identifiers) => {
    const overrides = getOverrides();
    for (const id of identifiers) {
        if (!id)
            continue;
        const override = overrides[id];
        if (override)
            return override;
    }
    return null;
};
const buildOverrideSld = (layerName, color) => {
    const safeLayerName = escapeXml(layerName);
    return [
        '<?xml version="1.0" encoding="UTF-8"?>',
        '<StyledLayerDescriptor version="1.0.0" ',
        'xmlns="http://www.opengis.net/sld" ',
        'xmlns:ogc="http://www.opengis.net/ogc" ',
        'xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" ',
        'xsi:schemaLocation="http://www.opengis.net/sld StyledLayerDescriptor.xsd">',
        '<NamedLayer>',
        `<Name>${safeLayerName}</Name>`,
        '<UserStyle>',
        '<Title>Color Override</Title>',
        '<FeatureTypeStyle>',
        '<Rule>',
        '<PointSymbolizer>',
        '<Graphic>',
        '<Mark>',
        '<WellKnownName>circle</WellKnownName>',
        '<Fill>',
        `<CssParameter name="fill">${color}</CssParameter>`,
        `<CssParameter name="fill-opacity">${SLD_STYLE.pointFillOpacity}</CssParameter>`,
        '</Fill>',
        '<Stroke>',
        `<CssParameter name="stroke">${color}</CssParameter>`,
        `<CssParameter name="stroke-opacity">${SLD_STYLE.strokeOpacity}</CssParameter>`,
        `<CssParameter name="stroke-width">${SLD_STYLE.pointStrokeWidth}</CssParameter>`,
        '</Stroke>',
        '</Mark>',
        `<Size>${SLD_STYLE.pointSize}</Size>`,
        '</Graphic>',
        '</PointSymbolizer>',
        '<LineSymbolizer>',
        '<Stroke>',
        `<CssParameter name="stroke">${color}</CssParameter>`,
        `<CssParameter name="stroke-opacity">${SLD_STYLE.strokeOpacity}</CssParameter>`,
        `<CssParameter name="stroke-width">${SLD_STYLE.lineWidth}</CssParameter>`,
        '</Stroke>',
        '</LineSymbolizer>',
        '<PolygonSymbolizer>',
        '<Fill>',
        `<CssParameter name="fill">${color}</CssParameter>`,
        `<CssParameter name="fill-opacity">${SLD_STYLE.fillOpacity}</CssParameter>`,
        '</Fill>',
        '<Stroke>',
        `<CssParameter name="stroke">${color}</CssParameter>`,
        `<CssParameter name="stroke-opacity">${SLD_STYLE.strokeOpacity}</CssParameter>`,
        `<CssParameter name="stroke-width">${SLD_STYLE.polygonStrokeWidth}</CssParameter>`,
        '</Stroke>',
        '</PolygonSymbolizer>',
        '</Rule>',
        '</FeatureTypeStyle>',
        '</UserStyle>',
        '</NamedLayer>',
        '</StyledLayerDescriptor>',
    ].join('');
};
export const applyWmsColorOverride = (options, identifiers) => {
    const overrideColor = getOverrideColor([
        identifiers.layerId,
        identifiers.layerKey,
        identifiers.name,
        identifiers.displayName,
        identifiers.wmsName,
    ]);
    if (!overrideColor) {
        return false;
    }
    const layerName = identifiers.wmsName || identifiers.layerKey || identifiers.layerId || 'layer';
    options.sld_body = buildOverrideSld(layerName, overrideColor);
    return true;
};
